import attachVideoElement, {
  AttachableElement
} from '../../common/Reporter/attachVideoElement';
import logger from '../../log';
import { FactoryOptionsBase, Handler } from '@kollective-frontend/ksdk-api';
import { dispatchPlayerEvent } from '../../events/dispatch';
import { CleanupMethod } from '../BaseInteraction';
import { noop } from '../../common/utils/callOrNoop';
import { NonStdPlayerEvents } from '../../common/Reporter/constants';

const log = logger.getLogger(__filename);

const BitrateChangedEvent = NonStdPlayerEvents.playbackbitratechanged;

export interface BitrateState {
  lastBitrate?: number;
}
export interface BitrateStateOptions {
  state: BitrateState;
  getBitrate: (evt: unknown) => number;
}

export const handleBitrateChangeEvent = (
  opts: BitrateStateOptions,
  evt: unknown
): void => {
  const b = opts.getBitrate(evt);
  if (b !== opts.state.lastBitrate) {
    dispatchPlayerEvent({ e: BitrateChangedEvent.code, b });
    opts.state.lastBitrate = b;
  }
};

export interface MediaElementInfo {
  addEventListener: (name: string, handler: Handler<unknown>) => void;
  removeEventListener: (name: string, handler?: Handler<unknown>) => void;
  currentTime: number;
  playbackRate: number;
  duration: number;
}

export class FactoryAttachableElement implements AttachableElement {
  opts: FactoryOptionsBase;
  state: unknown;
  constructor(opts: FactoryOptionsBase) {
    this.opts = opts;
  }

  addEventListener(name: string, handler: Handler<unknown>): void {
    if (typeof this.opts.addEventListener === 'function') {
      this.opts.addEventListener(name, handler);
    } else {
      log.debug('No addEventListener defined');
    }
  }

  removeEventListener(name: string, handler?: Handler<unknown>): void {
    this.state = `removeEventListener: ${name}`;
    const element = this._getElement();
    if (typeof element.removeEventListener !== 'function') {
      log.debug('Not removing event', name, 'no remove event listener found');
      return;
    }
    element.removeEventListener(name, handler);
  }

  private _getElement(): MediaElementInfo {
    const { getMediaElement, getVideoElement } = this.opts;
    const element =
      (typeof getMediaElement === 'function' && getMediaElement()) ||
      (typeof getVideoElement === 'function' && getVideoElement()) ||
      ({} as unknown as MediaElementInfo);

    if (typeof element.addEventListener !== 'function') {
      log.debug(
        'Will be unable to attach to events as no addEventListener set'
      );
      element.addEventListener = () => undefined;
    }
    const ele = element as unknown as Record<string, unknown>;
    ['currentTime', 'duration', 'playbackRate'].forEach(item => {
      if (typeof ele[item] === 'undefined') {
        log.debug(
          `Will be unable to get required information: ${item} as no ${item} element set`
        );
        ele[item] = NaN;
      }
    });
    return element as MediaElementInfo;
  }

  get currentTime(): number {
    return this._getElement().currentTime;
  }

  get duration(): number {
    return this._getElement().duration;
  }

  get playbackRate(): number {
    return this._getElement().playbackRate;
  }
}

const bindFactoryPlayerEvents = (
  options: FactoryOptionsBase
): CleanupMethod => {
  if (typeof options?.addEventListener !== 'function') {
    log.warn('Unable to bind player events');
    return noop;
  }
  const methods: CleanupMethod[] = [];
  const cleanup = (): void => {
    while (methods.length > 0) {
      const method = methods.shift();
      if (method) {
        method();
      }
    }
  };
  if (options.rebindEventName) {
    options.addEventListener(options.rebindEventName, () => {
      cleanup();
      _bindFactoryPlayerEvents(options, methods);
    });
  }
  _bindFactoryPlayerEvents(options, methods);
  return cleanup;
};

const _bindFactoryPlayerEvents = (
  options: FactoryOptionsBase,
  methods: CleanupMethod[]
): void => {
  // We do all the attachment here as we don't have access to the actual media
  // element.
  const attachable = new FactoryAttachableElement(options);
  const cleanupVideoElement = attachVideoElement(attachable);

  const state: BitrateState = {};
  const handlerMap: Map<string, Handler<unknown>> = new Map();
  if (options.bitrateChangedEventName && options.getCurrentBitrate) {
    const bitrateChangedEvent = options.bitrateChangedEventName;
    const getBitrate = options.getCurrentBitrate as (evt: unknown) => number;
    const handler = (evt: unknown) => {
      handleBitrateChangeEvent({ getBitrate, state }, evt);
    };
    handlerMap.set(bitrateChangedEvent, handler);
    attachable.addEventListener(bitrateChangedEvent, handler);
  }
  methods.push(() => {
    cleanupVideoElement();
    handlerMap.forEach((handler, name) => {
      attachable.removeEventListener(name, handler);
    });
    handlerMap.clear();
  });
};

export default bindFactoryPlayerEvents;
